import type React from 'react';

import colors from '@packages/core/styles/colors';
import { getLogger } from '@packages/core/logging';
import { Button, Icon, Label, Typography } from '@packages/ui/shared';
import { CanvasAction, type EditorApi, ExportFormat, ImageFormat, PhotoEditorSDKUI, Tool } from 'photoeditorsdk';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';

const logger = getLogger('web');

export type EditAssetProp = {
    assetId: string;
    assetTypeId?: string;
    downloadUrl?: string;
    media?: { assetImageTypeId: string; url: string }[];
    name: string;
    note?: string;
    preview?: string;
};

type AssetEditorModalProps = {
    assets: EditAssetProp[];
    edit?: boolean;
    initialIndex?: number;
    onClose: () => void;
    onDeleteAsset?: (assetId: string) => void;
    onDownload?: (imageSrc: string, fileName: string) => void;
    onUpdateAsset?: (assetId: string, update: { name?: string; note?: string; blob?: File }) => void;
};

const styles = StyleSheet.create({
    saveButton: {
        color: colors.white,
    },
    deleteButton: {
        color: colors.redOne,
    },
    footerDeleteBtn: {
        borderColor: colors.redOne,
        borderRadius: 4,
        borderStyle: 'solid',
        borderWidth: 1,
        flex: 1,
    },
    prevAndNextBtn: {
        color: colors.white,
        fontSize: 14,
        marginLeft: 8,
    },
    imgBtn: {
        color: colors.white,
        fontSize: 14,
    },
    labelForm: {
        marginTop: 16,
    },
    footerBtn: {
        flex: 1,
    },
});

const useStyles = createUseStyles({
    overlay: {
        alignItems: 'stretch',
        backgroundColor: 'rgba(0, 0, 0, 0.92)',
        bottom: 0,
        display: 'flex',
        flexDirection: 'row-reverse',
        position: 'fixed',
        right: 0,
        top: 0,
        width: '20%',
        zIndex: 2000,
    },
    viewerSection: {
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
    },
    closeBtn: {
        cursor: 'pointer',
        position: 'absolute',
        right: 24,
        top: 24,
        zIndex: 1110,
    },
    closeBtnSdk: {
        cursor: 'pointer',
        position: 'absolute',
        right: 20,
        top: 16,
        zIndex: 9999999,
    },
    saveBtnSdk: {
        position: 'absolute',
        bottom: 20,
        left: 60,
        zIndex: 2000,
        width: '220px',
    },
    navButtons: {
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        borderRadius: 8,
        bottom: 16,
        display: 'flex',
        gap: 16,
        justifyContent: 'center',
        left: '48%',
        position: 'absolute',
        right: '62%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
    },
    editorSection: {
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: 16,
        position: 'fixed',
        right: 0,
        top: 0,
        width: '20%',
        zIndex: 1000,
    },
    input: {
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
        fontSize: 14,
        padding: 8,
        width: '100%',
    },
    buttonActions: {
        display: 'flex',
        flexDirection: 'row',
        gap: 12,
        marginTop: 'auto',
    },
    editorTitle: {
        borderBottom: `1px solid ${colors.grayThree}`,
        fontSize: 20,
        fontWeight: 'bold',
        paddingBottom: 16,
    },
    editorActions: {
        display: 'flex',
        gap: 12,
        items: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
    },
    actionItem: {
        alignItems: 'center',
        cursor: 'pointer',
        display: 'flex',
        fontSize: 14,
        gap: 6,
        userSelect: 'none',
        '&:hover': {
            opacity: 0.8,
        },
    },
    errorText: {
        color: colors.redOne,
        fontSize: 12,
        marginTop: 4,
    },
    sdkContainer: {
        bottom: 0,
        left: 0,
        position: 'fixed',
        right: 0,
        top: 0,
        width: '80%',
        zIndex: 1111,
    },
});

const AssetEditorModal: React.FC<AssetEditorModalProps> = ({
    assets,
    edit,
    initialIndex = 0,
    onClose,
    onDeleteAsset,
    onDownload,
    onUpdateAsset,
}) => {
    const classes = useStyles();
    const sdkContainerRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<EditorApi | null>(null);

    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [editedName, setEditedName] = useState('');
    const [editedNote, setEditedNote] = useState('');
    const [nameError, setNameError] = useState('');
    const [pendingEditorInit, setPendingEditorInit] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [isDownloading, setIsDownloading] = useState(false);

    const currentAsset = assets[currentIndex];

    const getImageUrl = (asset: EditAssetProp): string => {
        const originalMedia = asset.media?.find((m) => m.assetImageTypeId === 'ORIGINAL');
        return originalMedia?.url || asset.preview || asset.downloadUrl || '';
    };

    const handleResizeImage = () => {
        setPendingEditorInit(true);
    };

    useEffect(() => {
        if (currentAsset) {
            setEditedName(currentAsset.name || '');
            setEditedNote(currentAsset.note || '');
            setNameError('');
        }
    }, [currentAsset]);

    useEffect(() => {
        const initEditor = async () => {
            if (!pendingEditorInit || !sdkContainerRef.current) {
                return;
            }
            setPendingEditorInit(false);

            try {
                if (editorRef.current) {
                    editorRef.current.dispose();
                }

                const editor = await PhotoEditorSDKUI.init({
                    container: sdkContainerRef.current,
                    image: getImageUrl(currentAsset),
                    license: JSON.stringify(require('@web/pesdk_html5_license.json')),
                    mainCanvasActions: [CanvasAction.UNDO, CanvasAction.REDO],
                    assetBaseUrl: '/photoeditorsdk/assets/',
                    defaultTool: Tool.TRANSFORM,
                    tools: [
                        Tool.TRANSFORM,
                        Tool.ADJUSTMENT,
                        Tool.BRUSH,
                        Tool.LIBRARY,
                        Tool.FILTER,
                        Tool.TEXT,
                        Tool.STICKER,
                        Tool.FRAME,
                        Tool.OVERLAY,
                    ],
                    [Tool.TRANSFORM]: {
                        enableRotation: false,
                        enableFlip: false,
                        categories: [
                            {
                                identifier: 'imgly_transforms_common',
                                items: [
                                    { identifier: 'imgly_transform_common_square' },
                                    { identifier: 'imgly_transform_common_4' },
                                    { identifier: 'imgly_transform_common_16' },
                                    { identifier: 'imgly_transform_common_3' },
                                    { identifier: 'imgly_transform_common_9' },
                                ],
                            },
                        ],
                    },
                    export: {
                        image: {
                            exportType: ExportFormat.BLOB,
                            quality: 1,
                            enableDownload: false,
                        },
                    },
                    enableZoom: false,
                    custom: {
                        components: {
                            buttons: {
                                toolControlBarSecondaryButton: () => <></>,
                            },
                        },
                    },
                });

                editorRef.current = editor;

                (editor as any).on('close', () => {
                    if (confirm('You haven’t saved the edited image. Discard changes?')) {
                        editor.dispose();
                    }
                });
            } catch (err) {
                logger.error('Failed to initialize the PhotoEditorSDK editor', { error: err });
            }
        };

        initEditor();
    }, [pendingEditorInit, currentAsset]);

    useEffect(() => {
        if (edit) {
            handleResizeImage();
        }
    }, [edit]);

    const handleDelete = (assetId: string) => {
        onDeleteAsset?.(assetId);
        onClose();
    };

    const handleSave = async () => {
        setIsSaving(true);
        const fileExt = currentAsset.name?.split('.').pop()?.toLowerCase();
        const newExt = editedName.split('.').pop()?.toLowerCase();

        if (!editedName) {
            setNameError('Name cannot be empty');
            setIsSaving(false);
            return;
        }
        if (fileExt !== newExt) {
            setNameError('File extension must match original');
            setIsSaving(false);
            return;
        }

        const updated: { name: string; note: string; blob?: File } = { name: editedName, note: editedNote };
        const editor = editorRef.current;
        if (editor) {
            const blob: any = await editor.export({ format: ImageFormat.PNG });
            const file = new File([blob], currentAsset.name, { type: blob.type });
            updated.blob = file;
        }

        await onUpdateAsset?.(currentAsset.assetId, updated);

        setIsSaving(false);
        onClose();
    };

    return ReactDOM.createPortal(
        <>
            <div className={classes.sdkContainer} ref={sdkContainerRef} />
            <div data-testid="download-button" style={downloadButtonStyle}>
                <Button
                    isLoading={isDownloading}
                    onPress={async () => {
                        setIsDownloading(true);
                        await onDownload?.(getImageUrl(currentAsset), currentAsset.name);
                        setIsDownloading(false);
                    }}
                    variant="link-header-button"
                >
                    <Icon color="grayFive" name="download" size={18} />
                </Button>
            </div>

            <div className={classes.closeBtnSdk} onClick={onClose}>
                <Icon color="black" name="x-close" size={24} />
            </div>

            <div className={classes.editorSection}>
                <p className={classes.editorTitle}>Edit Attachment</p>
                <Label style={styles.labelForm}>File Name</Label>
                <input
                    className={classes.input}
                    id="asset-name"
                    onChange={(e) => setEditedName(e.target.value)}
                    type="text"
                    value={editedName}
                />
                {nameError && <div className={classes.errorText}>{nameError}</div>}
                <Label style={styles.labelForm}>Description</Label>
                <textarea
                    className={classes.input}
                    id="asset-note"
                    onChange={(e) => setEditedNote(e.target.value)}
                    rows={3}
                    value={editedNote}
                />
                <div className={classes.buttonActions}>
                    <Button
                        iconLeft={<Icon color="redOne" name="trash-striped" size={18} />}
                        onPress={() => handleDelete(currentAsset.assetId)}
                        style={styles.footerDeleteBtn}
                        titleStyle={styles.deleteButton}
                        variant="ghost-blue"
                    >
                        Delete
                    </Button>
                    <Button isLoading={isSaving} onPress={handleSave} style={styles.footerBtn} variant="primary">
                        Save
                    </Button>
                </div>
            </div>

            <div className={classes.navButtons}>
                <Button
                    disabled={currentIndex === 0}
                    onPress={() => {
                        if (editorRef.current) {
                            editorRef.current.dispose();
                        }
                        setCurrentIndex((i) => Math.max(i - 1, 0));
                        setPendingEditorInit(true);
                    }}
                    variant="secondary"
                >
                    <Icon color="white" name="chevron-left" size={16} />
                    <Typography style={styles.prevAndNextBtn}>Previous</Typography>
                </Button>
                <Typography style={styles.imgBtn}>
                    Image {currentIndex + 1} of {assets.length}
                </Typography>
                <Button
                    disabled={currentIndex === assets.length - 1}
                    onPress={() => {
                        if (editorRef.current) {
                            editorRef.current.dispose();
                        }
                        setCurrentIndex((i) => Math.min(i + 1, assets.length - 1));
                        setPendingEditorInit(true);
                    }}
                    variant="secondary"
                >
                    <Typography style={styles.prevAndNextBtn}>Next</Typography>
                    <Icon color="white" name="chevron-right" size={16} />
                </Button>
            </div>
        </>,
        document.body
    );
};

const downloadButtonStyle = { position: 'fixed' as const, zIndex: 29999, top: 16, right: 'calc(20% + 16px)' };

export default AssetEditorModal;
